import React, { useRef, useState } from 'react';
import { findingsAPI } from '../../api/traceability';
import { errorMessage } from '../../lib/evaluationIntegrity';
import { getEvidenceUrl } from '../../lib/fileUrls';
import { captureLocation, evidenceStamp, uniqueEvidence } from '../../lib/fieldOperation';
import { hasMitigationEvidence, personName, STAGES, findingPermissions, FINDING_STATUS } from '../../lib/traceability';
import ZoneSelector from '../ZoneSelector';
import { Field } from './Fields';

export function EvidenceList({ items = [] }) {
  return <ul className="space-y-2">{uniqueEvidence({ evidence: items }).map((item, index) => (
    <li key={item.id || index}>
      <a className="text-blue-700 underline"
        href={getEvidenceUrl(item.file_url || item.url)} target="_blank" rel="noreferrer">
        Evidencia {index + 1}
      </a>
      <p className="text-xs">{evidenceStamp(item)}</p>
      {item.geo && <p className="text-xs">{item.geo.lat}, {item.geo.lng}</p>}
    </li>
  ))}</ul>;
}

export default function FindingCard({ finding, user, event, zones = [], onChanged }) {
  const permissions = findingPermissions(finding, user, event);
  const [description, setDescription] = useState('');
  const actionRequestId = useRef(null);
  const actionCapturedAt = useRef(null);
  const [pendingEvidence, setPendingEvidence] = useState(null);
  const [notes, setNotes] = useState('');
  const [zone, setZone] = useState(null);
  const [stage, setStage] = useState('final');
  const [actionId, setActionId] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const run = async (operation) => {
    setBusy(true);
    setError('');
    try {
      await operation();
      await onChanged?.();
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };
  const upload = (capture) => run(async () => {
    // #Fase3-B3 — Reintentar conserva id y hora para no duplicar una carga cuya respuesta se perdió.
    const geo = capture.geo === undefined ? await captureLocation() : capture.geo;
    const pending = { ...capture, geo };
    setPendingEvidence(pending);
    if (!geo && !zone?.id) {
      const error = new Error('Selecciona una zona para cargar la evidencia sin GPS.');
      error.response = { data: { detail: error.message } };
      throw error;
    }
    await findingsAPI.evidence(finding.id, pending.file, {
      stage: pending.stage, action_id: pending.stage === 'action' ? pending.actionId : undefined,
      client_evidence_id: pending.id, captured_at: pending.captured_at,
      lat: geo?.lat, lng: geo?.lng, accuracy_m: geo?.accuracy_m,
      geo_source: geo?.source || 'none', zone_id: zone?.id, zone_detail: zone?.detail,
    });
    setPendingEvidence(null);
  });
  return (
    <article className="soem-card p-4 space-y-4">
      <h3 className="font-bold">{finding.code} {finding.title}</h3>
      <p>{{ critico: 'Crítico', alto: 'Alto', medio: 'Medio', bajo: 'Bajo' }[finding.severity]} · {STAGES[finding.stage]}
        {finding.kind === 'riesgo' && ' · Nuevo riesgo'}</p>
      <ol className="space-y-4 border-l-2 pl-4">
        <li><h4 className="font-semibold">Hallazgo inicial</h4><p>{finding.description}</p>
          <p>{finding.zone?.label} {finding.zone?.detail}</p>
          {finding.location && <p>{finding.location.lat}, {finding.location.lng}</p>}
        </li>
        <li><h4 className="font-semibold">Evidencia inicial</h4>
          <EvidenceList items={finding.initial_evidence} /></li>
        <li><h4 className="font-semibold">Acciones</h4>
          {(finding.actions || []).map((action) => <div key={action.id} className="my-2">
            <p>{action.description}</p><p className="text-xs">{personName(action, 'by')} · {action.at}</p>
            {action.evidence?.length > 0 && <h5>Evidencia de la corrección</h5>}
            <EvidenceList items={action.evidence} />
          </div>)}
        </li>
        <li><h4 className="font-semibold">Evidencia de la corrección</h4>
          <EvidenceList items={[...(finding.final_evidence || []), ...(finding.evidence || [])]} /></li>
        <li><h4 className="font-semibold">Estatus: {FINDING_STATUS[finding.status]}</h4>
          <p>{finding.validation_notes}</p>
          {finding.validated_by && <p>Validó: {personName(finding, 'validated_by')} · {finding.validated_at}</p>}
          {finding.proposed_by && <p>Propuso: {personName(finding, 'proposed_by')} · {finding.proposed_at}</p>}
        </li>
      </ol>
      <p>Recomendación: {finding.recommendation || 'Sin recomendación'}</p>
      {!!finding.later_compliance?.length && <p>
        Cumplimiento posterior registrado. Requiere validación para cerrar.
      </p>}
      {error && <p role="alert">{error}</p>}
      <fieldset disabled={busy} className="space-y-3">
        {permissions.action && <form onSubmit={(e) => {
          e.preventDefault();
          if (!actionRequestId.current) {
            actionRequestId.current = crypto.randomUUID();
            actionCapturedAt.current = new Date().toISOString();
          }
          run(async () => {
            await findingsAPI.action(finding.id, {
              id: actionRequestId.current, description, captured_at: actionCapturedAt.current,
            });
            actionRequestId.current = null;
            setDescription('');
          });
        }}>
          <Field label="Acción correctiva" required value={description} onChange={setDescription} multiline />
          <button className="soem-btn-primary p-2">Agregar acción</button>
        </form>}
        {permissions.evidence && <div className="space-y-2">
          <Field label="Destino de evidencia" value={stage} onChange={setStage}
            options={{ initial: 'Inicial', final: 'Final', action: 'Acción' }} />
          {stage === 'action' && <Field label="Acción" value={actionId} onChange={setActionId}
            options={{ '': 'Selecciona', ...Object.fromEntries((finding.actions || [])
              .map((action) => [action.id, action.description])) }} />}
          <ZoneSelector zones={zones} zone={zone} onChange={setZone} />
          <label className="block">Cargar evidencia
            <input type="file" disabled={!!pendingEvidence || stage === 'action' && !actionId} onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) upload({ file, stage, actionId,
                id: crypto.randomUUID(), captured_at: new Date().toISOString(),
              });
              e.target.value = '';
            }} />
          </label>
          {pendingEvidence && <div>
            <p>Archivo pendiente: {pendingEvidence.file.name}</p>
            <button type="button" onClick={() => upload(pendingEvidence)}>Reintentar evidencia</button>
            <button type="button" onClick={() => setPendingEvidence(null)}>Descartar carga pendiente</button>
          </div>}
        </div>}
        {permissions.propose && <button className="soem-btn-primary p-2"
          onClick={() => run(() => findingsAPI.propose(finding.id))}>Proponer Mitigado</button>}
        {permissions.validate && <div>
          <Field label="Notas de validación" value={notes} onChange={setNotes} multiline />
          {!hasMitigationEvidence(finding) && <p>Para marcarlo como mitigado, sube la evidencia final de la corrección.</p>}
          {['mitigado', 'no_mitigado'].map((result) => <button key={result}
            disabled={result === 'mitigado' && !hasMitigationEvidence(finding)}
            className="soem-btn-secondary p-2" onClick={() => run(() => findingsAPI.validate(finding.id, {
              result, notes,
            }))}>Validar {FINDING_STATUS[result]}</button>)}
        </div>}
        {permissions.reopen && <button className="soem-btn-secondary p-2"
          onClick={() => run(() => findingsAPI.reopen(finding.id))}>Reabrir</button>}
      </fieldset>
    </article>
  );
}
